/**
 * Password strength rules.
 *
 * Single source of truth for both the server (apps/api/src/auth/password.ts)
 * and the client (apps/web/src/routes/security.tsx live indicator). The
 * server is authoritative — the client uses the same rules only to give
 * the user fast feedback before submit.
 *
 * Applied to passwords being set or changed AFTER PR 5a ships. The
 * existing `hashPassword` keeps its 8-char floor so historical accounts
 * and the dev seed don't break — only new passwords go through the
 * stronger gate.
 *
 * Rules are intentionally short. Length is the biggest factor against
 * brute-force; the letter + digit requirements push back against trivial
 * `"aaaaaaaaaaaa"` / `"123456789012"` patterns without making the rule
 * set so complex users resort to predictable substitutions.
 */
export const PASSWORD_MIN_LENGTH = 12;
const HAS_LETTER = /[A-Za-z]/;
const HAS_NUMBER = /[0-9]/;
/**
 * Check a candidate password against the strength rules. Returns the first
 * failure reason; callers wanting per-rule status (for a live indicator)
 * should call `evaluatePasswordRules` instead.
 */
export function validatePasswordStrength(plaintext) {
    if (plaintext.length < PASSWORD_MIN_LENGTH) {
        return { ok: false, reason: 'too_short' };
    }
    if (!HAS_LETTER.test(plaintext)) {
        return { ok: false, reason: 'no_letter' };
    }
    if (!HAS_NUMBER.test(plaintext)) {
        return { ok: false, reason: 'no_number' };
    }
    return { ok: true };
}
export function evaluatePasswordRules(plaintext) {
    return [
        {
            rule: 'length',
            label: `At least ${PASSWORD_MIN_LENGTH} characters`,
            satisfied: plaintext.length >= PASSWORD_MIN_LENGTH,
        },
        {
            rule: 'letter',
            label: 'Contains at least one letter',
            satisfied: HAS_LETTER.test(plaintext),
        },
        {
            rule: 'number',
            label: 'Contains at least one number',
            satisfied: HAS_NUMBER.test(plaintext),
        },
    ];
}
//# sourceMappingURL=password.js.map